import {
  CampaignSimulationStepsIds,
  CampaignSimulationStepsUnion,
  StepDetails,
} from "types/stepper";
import { array, mixed, object, string } from "yup";

type ErrorType = {
  errors: { [key: string]: any };
};

export const checkTabErrors = (errors: ErrorType) => {
  const tabErrors: string[] = [];

  Object.keys(errors).forEach((keyErr) => {
    switch (keyErr) {
      case "transaction":
        tabErrors.push(CampaignSimulationStepsIds.TRANSACTION);
        break;
      case "customer":
        tabErrors.push(CampaignSimulationStepsIds.MEMBER);
        break;
      case "referrer":
        tabErrors.push(CampaignSimulationStepsIds.REFERRED_MEMBER);
        break;
      case "custom_event":
      case "internal_event":
        tabErrors.push(CampaignSimulationStepsIds.EVENT);
        break;
    }
  });

  return tabErrors;
};

export const markErrorSteps = (
  steps: StepDetails<CampaignSimulationStepsUnion>[],
  formError: ErrorType,
) => {
  const errors = checkTabErrors(formError);
  return steps.map((step) => {
    const isStepError = errors.includes(step.id);

    return {
      ...step,
      status: step.status,
      hasError: isStepError,
    };
  });
};

export const isValueProvided = (value?: string | number) => {
  if (typeof value === "number") {
    return true;
  }

  return !!value;
};

export const isAnyValueProvided = (values: any[]) =>
  values.some((val) => isValueProvided(val));

export const getValidationSchemaByStep = (
  activeStep: CampaignSimulationStepsUnion,
  message: any,
) => {
  switch (activeStep) {
    case CampaignSimulationStepsIds.MEMBER:
      return object().shape({
        customer: object().shape({
          labels: array().of(
            object().shape({
              key: string().required(message({ id: "requiredFieldError" })),
              value: string().required(message({ id: "requiredFieldError" })),
            }),
          ),
        }),
      });
    case CampaignSimulationStepsIds.REFERRED_MEMBER:
      return object().shape({
        referrer: object().shape({
          labels: array().of(
            object().shape({
              key: string().required(message({ id: "requiredFieldError" })),
              value: string().required(message({ id: "requiredFieldError" })),
            }),
          ),
        }),
      });
    case CampaignSimulationStepsIds.TRANSACTION:
      return object().shape({
        trigger: string().required(message({ id: "requiredFieldError" })),
        transaction: object().shape({
          documentNumber: string().required(
            message({ id: "requiredFieldError" }),
          ),
          purchasedAt: string().required(message({ id: "requiredFieldError" })),
          items: array()
            .of(
              object().shape({
                labels: array().of(
                  object().shape({
                    key: string().required(
                      message({ id: "requiredFieldError" }),
                    ),
                    value: string().required(
                      message({ id: "requiredFieldError" }),
                    ),
                  }),
                ),
              }),
            )
            .min(1, message({ id: "requiredArray" }))
            .required(message({ id: "requiredArray" })),
          labels: array().of(
            object().shape({
              key: string().required(message({ id: "requiredFieldError" })),
              value: string().required(message({ id: "requiredFieldError" })),
            }),
          ),
          linkedDocumentNumber: string().test(
            "is-required-if-return_transaction",
            message({ id: "requiredFieldError" }),
            function (value) {
              const { trigger } = this.options.context || {};
              return trigger === "return_transaction" ? !!value : true;
            },
          ),
        }),
      });
    case CampaignSimulationStepsIds.EVENT: {
      return object().shape({
        internal_event: object()
          .shape({
            eventDate: string().required(message({ id: "requiredFieldError" })),
            type: string().required(message({ id: "requiredFieldError" })),
          })
          .when("trigger", (value, schema, data) => {
            const trigger = data.parent.trigger;
            return trigger === "internal_event"
              ? schema
              : mixed().notRequired();
          }),
        custom_event: object()
          .shape({
            eventDate: string().required(message({ id: "requiredFieldError" })),
            type: string().required(message({ id: "requiredFieldError" })),
          })
          .when("trigger", (value, schema, data) => {
            const trigger = data.parent.trigger;
            return trigger === "custom_event" ? schema : mixed().notRequired();
          }),
      });
    }
  }
};
